'use client';

import { GitBranch, Clock, ChevronRight } from 'lucide-react';
import { Project, projects } from './mockData';

interface ProjectListProps {
  onSelectProject: (project: Project) => void;
}

export function ProjectList({ onSelectProject }: ProjectListProps) {
  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-text mb-2">Projects</h1>
        <p className="text-text-muted">Track specifications across your repositories</p>
      </div>

      {/* Project Cards */}
      <div className="space-y-3">
        {projects.map((project) => (
          <ProjectCard
            key={project.id}
            project={project}
            onClick={() => onSelectProject(project)}
          />
        ))}
      </div>
    </div>
  );
}

interface ProjectCardProps {
  project: Project;
  onClick: () => void;
}

function ProjectCard({ project, onClick }: ProjectCardProps) {
  return (
    <button
      onClick={onClick}
      className="w-full text-left p-4 bg-bg-card rounded-lg border border-border/50 hover:border-primary/50 hover:bg-bg-card/80 transition-all group"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          {/* Project Name */}
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-lg font-medium text-text group-hover:text-primary transition-colors">
              {project.name}
            </h3>
            <ChevronRight className="w-4 h-4 text-text-muted opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>

          {/* Repo Path */}
          <div className="flex items-center gap-2 text-text-muted text-sm mb-3">
            <GitBranch className="w-4 h-4" />
            <span className="font-mono">{project.repo}</span>
          </div>

          {/* Progress Bar */}
          <PhaseProgress phases={project.phases} />
        </div>

        {/* Last Updated */}
        <div className="flex items-center gap-1.5 text-text-muted text-sm whitespace-nowrap">
          <Clock className="w-4 h-4" />
          <span>{project.lastUpdated}</span>
        </div>
      </div>
    </button>
  );
}

interface PhaseProgressProps {
  phases: Project['phases'];
}

function PhaseProgress({ phases }: PhaseProgressProps) {
  // Color progression: fuchsia → violet → indigo
  const phaseConfig = [
    { key: 'specify', label: 'Specify', color: 'bg-fuchsia-400' },
    { key: 'plan', label: 'Plan', color: 'bg-violet-500' },
    { key: 'tasks', label: 'Tasks', color: 'bg-indigo-600' },
  ] as const;

  return (
    <div className="flex items-center gap-1">
      {phaseConfig.map((phase, index) => {
        const status = phases[phase.key];
        const isComplete = status === 'complete';
        const isInProgress = status === 'in-progress';

        return (
          <div key={phase.key} className="flex items-center gap-1">
            {/* Phase Segment */}
            <div className="flex items-center gap-1.5">
              {/* Status Indicator */}
              <div
                className={`w-2 h-2 rounded-full ${
                  isComplete
                    ? phase.color
                    : isInProgress
                    ? `${phase.color} animate-pulse`
                    : 'bg-border'
                }`}
              />

              {/* Label */}
              <span
                className={`text-xs ${
                  isComplete || isInProgress ? 'text-text-secondary' : 'text-text-muted'
                }`}
              >
                {phase.label}
              </span>

              {/* Progress Line */}
              <div
                className={`w-8 h-0.5 rounded ${
                  isComplete ? phase.color : 'bg-border'
                }`}
              />
            </div>

            {/* Connector (not on last item) */}
            {index < phaseConfig.length - 1 && (
              <div className="w-1" />
            )}
          </div>
        );
      })}
    </div>
  );
}
